import { useCallback, useEffect, useRef, useState } from 'react';

const STAGE_RULES_SEEN_PREFIX = 'stage_rules_seen';

export function getStageRulesList(stage) {
  const rules = Array.isArray(stage?.rules) ? stage.rules : [];
  return rules.filter((r) => String(r ?? '').trim().length > 0);
}

function stageRulesSeenKey(stageNumber, userId, sessionId) {
  return `${STAGE_RULES_SEEN_PREFIX}_${stageNumber}_${userId}_${sessionId}`;
}

function hasSeenStageRules(stageNumber, userId, sessionId) {
  if (!userId || !sessionId) return false;
  try {
    return sessionStorage.getItem(stageRulesSeenKey(stageNumber, userId, sessionId)) === '1';
  } catch {
    return false;
  }
}

function markStageRulesSeen(stageNumber, userId, sessionId) {
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stageRulesSeenKey(stageNumber, userId, sessionId), '1');
  } catch {
    /* ignore */
  }
}

/**
 * Auto-open stage rules the first time a user visits a stage in this session.
 * After that, rules open only via the ? button (openRules).
 */
export function useStageRulesModal({
  stageNumber,
  rulesList = [],
  isConfigured = true,
  submitted = false,
  alreadyPlayed = false,
  userId,
  sessionId,
  ready = true,
  skipAutoOpen = false,
}) {
  const [showRules, setShowRules] = useState(false);
  const [rulesDismissed, setRulesDismissed] = useState(() => {
    if (rulesList.length === 0) return true;
    return hasSeenStageRules(stageNumber, userId, sessionId);
  });
  const promptedRef = useRef(false);

  const hasRules = rulesList.length > 0;

  useEffect(() => {
    if (promptedRef.current || submitted || alreadyPlayed || !isConfigured || !ready || skipAutoOpen) {
      return undefined;
    }

    if (!userId || !sessionId) return undefined;

    if (!hasRules) {
      promptedRef.current = true;
      setRulesDismissed(true);
      return undefined;
    }

    if (hasSeenStageRules(stageNumber, userId, sessionId)) {
      promptedRef.current = true;
      setRulesDismissed(true);
      return undefined;
    }

    promptedRef.current = true;
    setShowRules(true);
    return undefined;
  }, [
    alreadyPlayed,
    hasRules,
    isConfigured,
    ready,
    rulesList,
    sessionId,
    skipAutoOpen,
    stageNumber,
    submitted,
    userId,
  ]);

  const openRules = useCallback(() => {
    setShowRules(true);
  }, []);

  const closeRules = useCallback(() => {
    setShowRules(false);
    markStageRulesSeen(stageNumber, userId, sessionId);
    setRulesDismissed(true);
  }, [sessionId, stageNumber, userId]);

  return { showRules, openRules, closeRules, rulesDismissed };
}
